import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { apiAuth, apiClient } from "../utils/http";
import { STORAGE_KEYS } from "../utils/constants";

const AuthContext = createContext(null);

function readStored(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEYS.token));
  const [user, setUser] = useState(() => readStored(STORAGE_KEYS.user));
  const [store, setStore] = useState(() => readStored(STORAGE_KEYS.store));
  const [session, setSession] = useState(() => readStored(STORAGE_KEYS.session));
  const sessionRequestRef = useRef(null);

  const login = useCallback(async (username, password) => {
    // POST pos/v1/login — App\Http\Controllers\api\admin\v1\AuthController@login,
    // same controller backing admin/v1/login, guarded by Auth::attempt(user_type
    // in [super_admin, store_owner, store_user], active=1). A waiter account is
    // a store_user with a role granting access pos / add order / list tables /
    // list products — there's no separate "waiter" auth type on the backend.
    const res = await apiAuth.post("login", { username, password });
    const { token: t, user: u, store: s } = res.data;
    localStorage.setItem(STORAGE_KEYS.token, t);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(u));
    localStorage.setItem(STORAGE_KEYS.store, JSON.stringify(s));
    // Drop whatever the previous session left behind so ensureSession() runs a
    // fresh init for this user — a different account can mean a different
    // store, different categories and different table areas.
    localStorage.removeItem(STORAGE_KEYS.session);
    setToken(t);
    setUser(u);
    setStore(s);
    setSession(null);
    return u;
  }, []);

  // pos/v1/init is a bootstrap call, not a per-screen one: it returns the store,
  // the user with their permissions, reminders, table areas and both order lists
  // alongside the menu. So it runs once — right after a login, or on a session
  // where the locally stored store/session data is missing (fresh device,
  // cleared storage) — and every screen after that fetches only what it needs
  // (Home pulls pos/v1/products, Cart pulls pos/v1/table-areas, the order tabs
  // pull pos/v1/orders/get-orders).
  const ensureSession = useCallback(async () => {
    if (!localStorage.getItem(STORAGE_KEYS.token)) return null;
    if (readStored(STORAGE_KEYS.store) && readStored(STORAGE_KEYS.session)) return null;
    // One in-flight init shared by every caller: several screens mounting at
    // once (or a re-render) must not each fire the bootstrap.
    if (sessionRequestRef.current) return sessionRequestRef.current;

    sessionRequestRef.current = apiClient
      .get("init")
      .then((res) => {
        const data = res.data || {};
        const next = { categories: data.categories || [], tableAreas: data.table_areas || [] };
        localStorage.setItem(STORAGE_KEYS.session, JSON.stringify(next));
        setSession(next);
        if (data.store) {
          localStorage.setItem(STORAGE_KEYS.store, JSON.stringify(data.store));
          setStore(data.store);
        }
        return next;
      })
      .finally(() => {
        // Cleared either way, so a failed bootstrap is retried by the next
        // screen that mounts instead of being remembered as done.
        sessionRequestRef.current = null;
      });

    return sessionRequestRef.current;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.store);
    localStorage.removeItem(STORAGE_KEYS.session);
    localStorage.removeItem(STORAGE_KEYS.menu);
    setToken(null);
    setUser(null);
    setStore(null);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, user, store, session, isAuthenticated: !!token, login, logout, ensureSession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
